import { ComponentPropsWithoutRef, forwardRef } from "react";

import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { Button, Group, Select, SelectProps, Text } from "@mantine/core";
import {
  BracketsEllipses,
  BracketsPlus,
} from "~/../../base-components/lib/assets/icons.generated";

import {
  Contract2,
  ContractEntityType,
  NftContractStandard,
} from "apollo/graphql.generated";

interface ContractItem {
  value: string; // id is contractId or "new"
  label: string;
  address?: string;
  contractType?: ContractEntityType;
  onClick?(): void;
}

type ItemProps = ContractItem & ComponentPropsWithoutRef<"div">;

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, label, address, contractType, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group position="apart">
        <Group
          pt={value.length === 0 ? 8 : 0}
          pb={value.length === 0 ? 8 : 0}
          noWrap
          spacing={8}
        >
          {value === "create" && <BracketsPlus />}
          <div>
            <Text size="sm">
              {label}
              {value === "new" && (
                <span>
                  {" "}
                  · <span style={{ fontWeight: 550 }}>New</span>
                </span>
              )}
            </Text>
            {value !== "create" && (
              <>
                <Text size="xs" opacity={0.6}>
                  MultipleEditions (
                  {contractType === ContractEntityType.EditionsDFS1155
                    ? "ERC-1155"
                    : "ERC-721"}
                  )
                </Text>

                {address != null && (
                  <Text size={"xs"} c={"dimmed"}>
                    {address}
                  </Text>
                )}
              </>
            )}
          </div>
        </Group>
        {value === "new" && (
          <Button color={OUTLINE_COLOR} size="xs">
            Edit
          </Button>
        )}
      </Group>
    </div>
  )
);

type ContractSelectProps = {
  contracts: Contract2[];
  handleCreate: () => void;
  contractId: string | null;
  contractOnChange: (contractId: string | null) => void;
  newContract?: {
    edited: boolean;
    data: {
      name: string;
      symbol: string;
      usingOpenseaBlocklist: boolean;
      contractStandard: NftContractStandard;
    };
  };
} & Omit<SelectProps, "data">;

const ContractSelect = ({
  contracts,
  newContract,
  handleCreate,
  contractId,
  contractOnChange,
  ...rest
}: ContractSelectProps) => {
  const selectedContractIndex = contracts.findIndex(
    (contract) => contract.id === contractId
  );

  let contractItems: ContractItem[] = contracts.map((contract) => {
    return {
      value: contract.id,
      label: contract.name,
      address: contract.address ?? undefined,
      contractType: contract.contractType,
    };
  });

  if (selectedContractIndex > -1) {
    // move selected contract to front
    const selectedContractItem = contractItems[selectedContractIndex];
    contractItems = contractItems.filter(
      (contractItem) => contractItem !== selectedContractItem
    );
    contractItems.unshift(selectedContractItem);
  }
  const contractType =
    newContract?.data.contractStandard === NftContractStandard.ERC1155
      ? ContractEntityType.EditionsDFS1155
      : undefined;
  if (newContract?.edited) {
    contractItems.unshift({
      value: "new",
      label: newContract.data.name,
      contractType,
    });
  } else {
    contractItems.unshift({
      value: "create",
      label: "Create a new contract",
      contractType,
    });
  }

  return (
    <Select
      placeholder="Select a contract..."
      icon={<BracketsEllipses />}
      value={contractId}
      onChange={(contractId: string | null) => {
        if (contractId === "create") {
          contractOnChange(null);
          handleCreate();
        } else if (contractId === "new") {
          contractOnChange("new");
          handleCreate();
        } else {
          contractOnChange(contractId);
        }
      }}
      itemComponent={SelectItem}
      styles={{
        dropdown: {
          outline: "none",
          border: "none",
        },
      }}
      {...rest}
      data={contractItems}
    />
  );
};

export default ContractSelect;
